import React, { useState, useCallback } from 'react';
import { Record } from 'immutable';
import { useDispatch } from 'react-redux';
import pick from 'lodash/pick';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';

// Hooks
import { useToggle, useDeepEqualSelector } from 'Shared/utils/hooks';
import { useTranslation } from 'Shared/utils/hooks/use-translation';

// Utils
import { getSortedItems } from 'Shared/utils/sorting';
import * as MeetingItemUtils from 'Shared/utils/MeetingItem.utils';

// Helpers
import MeetingItemHelpers from 'Shared/model_helpers/MeetingItem.helper';

// Actions
import { createItemFromSourceItemAction } from 'Shared/actions/meetingItems';
import { addItem } from 'Shared/actions/todos';
import { updateSelectedUuids } from 'Shared/actions/selectedUuids';

// Selectors
import { getActionItemsList } from 'Shared/selectors/workspace';

// Types
import { MeetingItem } from 'Shared/types/MeetingItem.type';

// Components
import { WpCollapsible } from 'Wpds/components/collapsible';
import WpIcon from 'Wpds/components/icon/WpIcon';
import ActionItemsTable from 'Shared/components/ActionItems/ActionItemsTable/ActionItemsTable';
import WpButton from 'Wpds/components/button/WpButton';

// Styles
import './MeetingItemDetailsPanelActionItemList.scss';

const ActionItemWhiteList = Object.freeze([
  'id',
  'uuid',
  'parentUuid',
  'workspacePath',
  'parentText',
  'isHighPriority',
  'workspaceId',
  'assignmentAvatar',
  'position',
]);

const sortingMap = {
  actionItems: 'default',
  owner: 'default',
  workspace: 'default',
  dueDate: 'default',
};

type Props = {
  meetingItem: Record<MeetingItem>;
};

const MeetingItemDetailsPanelActionItemList = ({ meetingItem }: Props) => {
  const dispatch = useDispatch();

  const [isOpen, toggleIsOpen] = useToggle(true);
  const [selectedSort, setSelectedSort] = useState(sortingMap);
  const { tr } = useTranslation();

  const itemId = MeetingItemHelpers.getId(meetingItem);
  const workspaceId = MeetingItemHelpers.getWorkspaceId(meetingItem);
  const itemType = MeetingItemHelpers.getItemType(meetingItem);

  const isDiscussionTopic = MeetingItemUtils.isDiscussionTopic(itemType);

  const actionItems = useDeepEqualSelector(state =>
    getSortedItems({
      items: getActionItemsList(state).filter(({ associatedMeetingItemId }) => associatedMeetingItemId === itemId),
      selectedSort,
      withHighPriority: true,
      positionField: 'position',
    }).map(actionItem => pick(actionItem, ActionItemWhiteList))
  );
  const actionItemsArray = Array.isArray(actionItems) ? actionItems : [];

  const handleCreateItem = useCallback(
    ({ sourceItemUuid, itemUuid }) => {
      const dueBy = moment(new Date()).startOf('day').startOf('hour').add(7, 'days').add(19, 'hours').unix();

      dispatch(
        createItemFromSourceItemAction({
          sourceItemUuid,
          itemUuid,
          associatedMeetingItemId: itemId,
          isAssignCurrentUser: false,
          dueBy,
        })
      );
    },
    [dispatch, itemId]
  );

  const handleAddMeetingItem = useCallback(() => {
    const dueBy = moment(new Date()).startOf('day').startOf('hour').add(7, 'days').add(19, 'hours').unix();
    const itemUuid = uuidv4();

    dispatch(
      addItem({
        itemUuid,
        workspaceId,
        associatedMeetingItemId: itemId,
        position: 1,
        dueBy,
        isAssignCurrentUser: true,
      })
    );
    dispatch(updateSelectedUuids(itemUuid, { isOneSelected: true }));
  }, [dispatch, workspaceId, itemId]);

  const hasActionItems = actionItemsArray.length > 0;

  const getTitleLabel = () => {
    const countOfActionItems = actionItemsArray.length;
    if (countOfActionItems === 1) return tr('perform.meeting_item.action_item_list.count_one');
    if (countOfActionItems > 1) return tr('perform.meeting_item.action_item_list.count_many', `${countOfActionItems}`);
    return tr('perform.meeting_item.action_item_list.title');
  };

  if (!isDiscussionTopic) return null;
  const baseClassName = 'MeetingItemDetailsPanelActionItemList';
  return (
    <div className={baseClassName}>
      <div className={`${baseClassName}__header`}>
        <div className={`${baseClassName}__title-wrapper`} onClick={() => toggleIsOpen()}>
          <div className={`${baseClassName}__title-wrapper__icon`}>
            <WpIcon name="check-square" size="sm" />
          </div>
          <p className={`${baseClassName}__title-wrapper__title`}>{getTitleLabel()}</p>
          <WpIcon name={isOpen ? 'angle-down' : 'angle-right'} />
        </div>
        {isOpen && hasActionItems && (
          <WpButton size="sm" variant="tertiary" onClick={handleAddMeetingItem}>
            <WpIcon name="plus-circle" />
            {tr('perform.meeting_item.action_item_list.add_action_item')}
          </WpButton>
        )}
      </div>

      <WpCollapsible open={isOpen}>
        <div className={`${baseClassName}__action-item-list`}>
          {hasActionItems ? (
            <ActionItemsTable
              forbidDrag
              actionItems={actionItemsArray}
              selectedSort={selectedSort}
              setSelectedSort={setSelectedSort}
              onCreateItem={handleCreateItem}
              withOwnerSection={false}
              withWorkspaceSection={false}
              withDueDateSection={false}
              hideActionsAreaOnDesktop
              showTableHeaders={false}
              forceShowMobileActions={true}
              enableHideAnimation={false}
            />
          ) : (
            <div className={`${baseClassName}__empty`} onClick={handleAddMeetingItem}>
              {tr('perform.meeting_item.action_item_list.add_action_item')}
            </div>
          )}
        </div>
      </WpCollapsible>
    </div>
  );
};

export default MeetingItemDetailsPanelActionItemList;
